import { t } from "i18next";
import { jwtDecode } from "jwt-decode";

// The session's tokens are out of reach of the page's scripts (#112, VAPT
// Portal 13). The refresh token is in an HttpOnly cookie the server sets, which
// goes with the renewal only; the page never sees it. The cookie Grafana and the
// administration panel read the access token from is the server's too, HttpOnly.
// The page holds the access token here, in this tab's memory, for the
// Authorization header of its requests and the task-status socket: nothing of
// it is written to localStorage or to a cookie, so a reload starts without one
// and renews (index.jsx).
let accessToken = null;

export const getAccessToken = () => accessToken;

// The tabs of a browser share one session: one refresh token, in the browser's
// cookie, and the access token the latest renewal issued, which revokes the one
// before it. A tab that receives a token (sign-in, password change, renewal)
// hands it to the other tabs over this channel, in memory, and they use it from
// then on: otherwise each tab would revoke the others' token with a renewal of
// its own.
const SESSION_CHANNEL = "rf-sedra-session";
const channel =
  typeof BroadcastChannel !== "undefined"
    ? new BroadcastChannel(SESSION_CHANNEL)
    : null;

// That a tab handed out a token, and when: no token, only a mark that changes
// with every one. localStorage is shared by the tabs at once, the channel's
// messages arrive a moment later; a tab that waited for the renewal lock reads
// the mark to tell that the tab before it renewed, and waits for its token
// instead of renewing again (renewUnlessRenewed).
const HANDOVER_MARK = "sessionHandover";
const HANDOVER_WAIT_MS = 1000;
// The mark only has to differ from the one before; a counter does that without
// a random number (nothing about it is secret).
let handoverCount = 0;

const readMark = () => {
  try {
    return localStorage.getItem(HANDOVER_MARK);
  } catch {
    return null;
  }
};

let waitingForHandover = [];

if (channel) {
  channel.onmessage = (event) => {
    const handed = event.data?.accessToken;
    if (typeof handed !== "string") return;
    accessToken = handed;
    waitingForHandover.splice(0).forEach((resolve) => resolve(handed));
  };
}

// The token handed over by another tab, or null when none comes in `ms`.
const tokenFromAnotherTab = (ms) =>
  new Promise((resolve) => {
    const timer = setTimeout(() => {
      waitingForHandover = waitingForHandover.filter((w) => w !== waiter);
      resolve(null);
    }, ms);
    const waiter = (token) => {
      clearTimeout(timer);
      resolve(token);
    };
    waitingForHandover.push(waiter);
  });

// A token the server has just issued to this tab: kept here and handed to the
// other tabs.
export const setAccessToken = (token) => {
  accessToken = token;
  channel?.postMessage({ accessToken: token });
  try {
    handoverCount += 1;
    localStorage.setItem(HANDOVER_MARK, `${Date.now()}-${handoverCount}`);
  } catch {
    // Without the mark a waiting tab renews itself: one renewal more, no harm.
  }
};

// Forgotten at sign-out (authSlice's logout).
export const clearAccessToken = () => {
  accessToken = null;
  waitingForHandover = [];
};

export const isTokenExpired = (token) => {
  if (!token) return true;
  const { exp } = jwtDecode(token);
  if (!exp) return true;
  return Date.now() >= exp * 1000;
};

// The record of a signed-in browser in localStorage: the user's profile, no
// token. A sign-out removes it (authSlice's logout); a sign-in writes it. True
// when a change another tab made to localStorage (its `storage` event) took it
// away: the record removed, or the storage cleared. It must still be gone when
// this tab hears of it, so a sign-in that followed at once is not undone here.
const SESSION_RECORD = "user";

export const isSignedOutInAnotherTab = (event) =>
  event.storageArea === localStorage &&
  (event.key === null || event.key === SESSION_RECORD) &&
  event.newValue === null &&
  !localStorage.getItem(SESSION_RECORD);

// One renewal at a time. A page that loads several queries at once gets them
// all refused when the access token expires; each renewal returns a new pair
// and revokes the previous one, so renewals started side by side revoked each
// other's tokens and the retries failed. Callers that arrive while a renewal
// is under way get that renewal's result.
//
// One at a time across the tabs of this browser as well. The tabs share the
// refresh token's cookie, and each renewal replaces it and revokes the access
// token the other tabs hold. A tab that waited for the lock takes the token the
// tab before it renewed and handed over, instead of renewing once more. The
// browser holds one refresh token, so the portal never sends one a renewal has
// replaced: the server ends every session of an account that does (#111).
//
// A failed renewal is judged here, the same for every caller (see below).
let renewalInProgress = null;
const RENEWAL_LOCK = "rf-sedra-session-renewal";

export const refreshToken = () => {
  if (!renewalInProgress) {
    const seen = { token: getAccessToken(), mark: readMark() };
    renewalInProgress = acrossTabs(() => renewUnlessRenewed(seen))
      .catch(settleFailedRenewal)
      .finally(() => {
        renewalInProgress = null;
      });
  }
  return renewalInProgress;
};

// Only the server's refusal ends the session (#121). It refuses a renewal with
// 401 (the refresh token was spent, revoked, has expired or is missing) or 403
// (the account is no longer active, or a replaced refresh token came back and
// every session of the account was ended): the user is signed out, here,
// whoever asked for the renewal, and the other tabs follow (store.js, #118).
//
// Any other failure — no answer, a 5xx while a deploy restarts the backend, any
// other status — says nothing about the session, which the server still holds:
// counted as a refusal, it signed out every tab of every user whose access
// token had expired. The session is kept, and the renewal rejects with
// PortalUnreachableError; the request that needed the renewal fails with its
// message and may be tried again. Nothing here tries it again by itself. Sent
// again within a minute, a renewal whose answer was lost is answered with the
// pair it got (backend: replace_session_tokens).
class SessionRefusedError extends Error {}

export class PortalUnreachableError extends Error {
  constructor(cause) {
    // The texts load after the page starts, and a renewal can fail before
    // they have (a reload while the backend restarts): the English text then
    // stands in for the translation.
    super(
      t("common:portalUnreachable", {
        defaultValue: "The portal could not be reached; please try again.",
      }),
      { cause }
    );
    this.name = "PortalUnreachableError";
  }
}

// Signing out is the store's: authSlice's logout, which also empties the API
// caches. store.js hands it over once; the renewal alone decides when.
let signOut = () => {};

export const onSessionRefused = (callback) => {
  signOut = callback;
};

// The account's roles and permissions a renewal names, as the server holds
// them now (VAPT Portal 2), are the store's to keep (authSlice's
// grantsRenewed); store.js hands the callback over once, as it does the
// sign-out's.
let grantsNamed = () => {};

export const onGrantsRenewed = (callback) => {
  grantsNamed = callback;
};

const settleFailedRenewal = (error) => {
  if (error instanceof SessionRefusedError) {
    signOut();
    throw error;
  }
  throw new PortalUnreachableError(error);
};

const REFUSALS = [401, 403];

const acrossTabs = (task) =>
  typeof navigator !== "undefined" && navigator.locks?.request
    ? navigator.locks.request(RENEWAL_LOCK, task)
    : task();

const renewUnlessRenewed = async (seen) => {
  const current = getAccessToken();
  if (current && current !== seen.token) return current;
  if (readMark() !== seen.mark) {
    // Another tab renewed while this one waited for the lock: its token is on
    // its way over the channel.
    const handed = await tokenFromAnotherTab(HANDOVER_WAIT_MS);
    if (handed && handed !== seen.token) return handed;
  }
  return renew();
};

const renew = async () => {
  // The refresh token goes in its cookie, which the browser sends only when
  // asked to include its credentials: in development the API is another origin
  // than the page. No answer at all rejects here: not a refusal.
  const response = await fetch(
    `${import.meta.env.VITE_API_BASE_URL}/login/new_access_token`,
    {
      method: "POST",
      credentials: "include",
    }
  );

  if (!response.ok) {
    if (REFUSALS.includes(response.status)) {
      throw new SessionRefusedError("Failed to refresh token");
    }
    throw new Error(`The renewal was answered with ${response.status}`);
  }

  // A renewal rotates the pair: the new refresh token replaces the old one in
  // the browser's cookie, and the new access token comes in the answer, with
  // the account's roles and permissions. An answer without them leaves the
  // page's as they are.
  const data = await response.json();
  const { access_token, roles, permissions } = data.data;

  setAccessToken(access_token);
  if (Array.isArray(roles) && Array.isArray(permissions)) {
    grantsNamed({ roles, permissions });
  }

  return access_token;
};
